/* Liquid matte reeded glass: the brand's navy-to-yellow stripe ramp rendered live in WebGL as wide
   lensed panels with a frost that sharpens towards each edge, a thin polished rim, a slow sweep of
   light, and a soft sheen that follows the pointer. */

const vertexShader = 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }'

const fragmentShader = `
precision highp float;
uniform vec2 uRes;
uniform float uTime;
uniform vec2 uPtr;
uniform float uEnergy;
uniform float uFlute;
uniform vec2 uRange;
uniform vec2 uDir;
uniform float uShift;
uniform float uLight;

vec3 ramp(float t){
  t = clamp(t, 0.0, 1.0);
  vec3 c = vec3(0.012, 0.027, 0.173);
  c = mix(c, vec3(0.004, 0.204, 0.463), smoothstep(0.00, 0.18, t));
  c = mix(c, vec3(0.165, 0.333, 0.878), smoothstep(0.18, 0.34, t));
  c = mix(c, vec3(0.706, 0.784, 1.000), smoothstep(0.34, 0.46, t));
  c = mix(c, vec3(0.957, 0.937, 0.925), smoothstep(0.46, 0.54, t));
  c = mix(c, vec3(0.980, 0.741, 0.000), smoothstep(0.54, 0.64, t));
  c = mix(c, vec3(0.949, 0.663, 0.000), smoothstep(0.64, 0.82, t));
  c = mix(c, vec3(1.000, 0.827, 0.302), smoothstep(0.82, 1.00, t));
  return c;
}

vec3 rampLight(float t){
  t = clamp(t, 0.0, 1.0);
  vec3 c = vec3(1.0, 1.0, 1.0);
  c = mix(c, vec3(0.945, 0.957, 1.000), smoothstep(0.00, 0.22, t));
  c = mix(c, vec3(0.725, 0.796, 1.000), smoothstep(0.22, 0.36, t));
  c = mix(c, vec3(0.361, 0.502, 0.961), smoothstep(0.36, 0.46, t));
  c = mix(c, vec3(0.863, 0.898, 1.000), smoothstep(0.46, 0.53, t));
  c = mix(c, vec3(1.000, 0.945, 0.780), smoothstep(0.53, 0.60, t));
  c = mix(c, vec3(0.980, 0.741, 0.000), smoothstep(0.60, 0.70, t));
  c = mix(c, vec3(1.000, 0.835, 0.310), smoothstep(0.70, 0.86, t));
  c = mix(c, vec3(1.000, 0.925, 0.690), smoothstep(0.86, 1.00, t));
  return c;
}

vec3 pick(float t){ return uLight > 0.5 ? rampLight(t) : ramp(t); }

float field(vec2 uv){
  float aspect = uRes.x / uRes.y;
  float t = dot(uv - 0.5, uDir) + 0.5 + uShift;
  t += 0.070 * sin(uv.y * 3.0 + uTime * 0.23 + uv.x * 1.7);
  t += 0.035 * sin(uv.y * 7.0 - uTime * 0.31 + uv.x * 4.0);
  t += 0.050 * sin(uv.x * 2.3 + uTime * 0.13);
  t += 0.045 * sin(uv.x * 9.0 + uv.y * 1.6 + uTime * 0.19);
  t += 0.025 * sin(uv.x * 17.0 - uv.y * 2.2 - uTime * 0.27);
  vec2 d = (uv - uPtr) * vec2(aspect, 1.0);
  t += uEnergy * 0.17 * exp(-dot(d, d) * 4.5);
  return mix(uRange.x, uRange.y, t);
}

float hash(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

vec3 liquidMatte(vec2 frag){
  vec2 uv = frag / uRes;
  float fx = frag.x / uFlute;
  float id = floor(fx);
  float u = fract(fx) * 2.0 - 1.0;
  float au = abs(u);
  float cx = (id + 0.5) * uFlute;
  float prof = 0.95 * u + 0.55 * sign(u) * pow(au, 5.0);
  vec2 lensed = vec2((cx - prof * uFlute * 1.1) / uRes.x, uv.y + 0.025 * u * u);
  float clear = smoothstep(0.82, 0.99, au);
  float radius = mix(0.018, 0.005, clear);
  float rot = hash(floor(frag)) * 6.2831;
  vec3 acc = vec3(0.0);
  for (int i = 0; i < 7; i++) {
    float fi = float(i);
    float r = sqrt((fi + 0.5) / 7.0) * radius;
    float a = rot + fi * 2.39996;
    acc += pick(field(lensed + vec2(cos(a), sin(a) * 1.6) * r));
  }
  vec3 col = acc / 7.0;
  col = mix(col, vec3(0.975, 0.98, 1.0), 0.035 * uLight);
  float face = 0.5 + 0.5 * u;
  col *= 0.93 + 0.07 * smoothstep(0.0, 1.0, face);
  float sweep = exp(-pow((uv.x + 0.35 * uv.y - 0.55 - 0.18 * sin(uTime * 0.09)) * 3.2, 2.0));
  col += 0.035 * sweep * (0.6 + 0.4 * sqrt(max(0.0, 1.0 - u * u)));
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(vec3(lum), col, 1.14);
  vec2 d = (uv - uPtr) * vec2(uRes.x / uRes.y, 1.0);
  float sheen = exp(-dot(d, d) * 3.0) * uEnergy;
  float rim = smoothstep(0.955, 0.985, au) * (1.0 - smoothstep(0.992, 1.0, au));
  col += rim * (0.22 + 0.3 * sheen);
  col += sheen * 0.05;
  return col;
}

void main(){
  vec2 frag = gl_FragCoord.xy;
  vec3 col = liquidMatte(frag);
  col += (hash(frag * 1.37) - 0.5) * 0.018;
  gl_FragColor = vec4(col, 1.0);
}`

/* The light ramp used by the hero and the invitation. Narrow (portrait) panels turn the stripes
   to run across the panel instead of along it. */
const heroVariant = {
  light: 1,
  range: [0, 1],
  shift: -0.06,
  dir: [1.0, 0.28],
  dirNarrow: [0.3, -1.0],
  shiftNarrow: -0.1,
} as const

const uniformNames = ['uRes', 'uTime', 'uPtr', 'uEnergy', 'uFlute', 'uRange', 'uDir', 'uShift', 'uLight'] as const

export type ReededSurface = {
  /** Match the drawing buffer to the canvas size, then redraw. */
  resize: () => void
  /** Advance the animation by `dt` seconds and draw a frame. */
  frame: (dt: number) => void
  /** Pointer position over the panel, 0 to 1 from the bottom left. */
  point: (x: number, y: number) => void
  leave: () => void
  /** True while the pointer's sheen is still fading in or out. */
  isStirred: () => boolean
  dispose: () => void
}

/** Returns null when WebGL is unavailable, so the panel can fall back to its static stripes. */
export const createReededSurface = (canvas: HTMLCanvasElement): ReededSurface | null => {
  if (typeof WebGLRenderingContext === 'undefined') return null

  const gl = canvas.getContext('webgl', { antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: true })
  if (!gl) return null

  const compile = (type: number, source: string) => {
    const shader = gl.createShader(type)
    if (!shader) throw new Error('Unable to create shader')
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? 'Shader failed')
    return shader
  }

  const program = gl.createProgram()
  try {
    if (!program) throw new Error('Unable to create program')
    gl.attachShader(program, compile(gl.VERTEX_SHADER, vertexShader))
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragmentShader))
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) ?? 'Link failed')
  } catch {
    return null
  }

  gl.useProgram(program)
  const buffer = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
  const position = gl.getAttribLocation(program, 'p')
  gl.enableVertexAttribArray(position)
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)

  const uniforms = Object.fromEntries(uniformNames.map((name) => [name, gl.getUniformLocation(program, name)])) as Record<
    (typeof uniformNames)[number],
    WebGLUniformLocation | null
  >

  const state = {
    time: 4,
    pointer: [0.72, 0.62],
    target: [0.72, 0.62],
    energy: 0,
    energyTarget: 0,
    dpr: 1,
    narrow: false,
  }

  const draw = () => {
    if (!canvas.width) return
    const flute = (state.narrow ? 40 : 56) * state.dpr
    const dir = state.narrow ? heroVariant.dirNarrow : heroVariant.dir
    const shift = state.narrow ? heroVariant.shiftNarrow : heroVariant.shift
    gl.uniform2f(uniforms.uRes, canvas.width, canvas.height)
    gl.uniform1f(uniforms.uTime, state.time)
    gl.uniform2f(uniforms.uPtr, state.pointer[0], state.pointer[1])
    gl.uniform1f(uniforms.uEnergy, state.energy)
    gl.uniform1f(uniforms.uFlute, flute)
    gl.uniform2f(uniforms.uRange, heroVariant.range[0], heroVariant.range[1])
    gl.uniform2f(uniforms.uDir, dir[0], dir[1])
    gl.uniform1f(uniforms.uShift, shift)
    gl.uniform1f(uniforms.uLight, heroVariant.light)
    gl.drawArrays(gl.TRIANGLES, 0, 3)
  }

  return {
    resize: () => {
      const rect = canvas.getBoundingClientRect()
      state.dpr = Math.min(window.devicePixelRatio || 1, 1.25)
      const width = Math.max(1, Math.round(rect.width * state.dpr))
      const height = Math.max(1, Math.round(rect.height * state.dpr))
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
      }
      state.narrow = rect.width / Math.max(rect.height, 1) < 0.95
      gl.viewport(0, 0, width, height)
      draw()
    },
    frame: (dt) => {
      state.time += dt
      state.pointer[0] += (state.target[0] - state.pointer[0]) * 0.06
      state.pointer[1] += (state.target[1] - state.pointer[1]) * 0.06
      state.energy += (state.energyTarget - state.energy) * 0.04
      draw()
    },
    point: (x, y) => {
      state.target = [x, y]
      state.energyTarget = 1
    },
    leave: () => {
      state.energyTarget = 0
    },
    isStirred: () => state.energyTarget > 0 || state.energy > 0.002,
    dispose: () => {
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    },
  }
}
